import React, { memo } from 'react';
import styled from 'styled-components';

interface IMoneyCellProps {
  /** Amount in the document's native currency. `null`/`undefined` → «—». */
  amount?: number | null;
  currency?: string | null;
  /** True when the document represents a refund (correction or negative expense). */
  negative?: boolean;
  /** Outstanding amount; rendered with a red accent when `> 0`. */
  highlightOutstanding?: boolean;
  align?: 'left' | 'right';
}

const NUMBER_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatAmount = (amount: number) => {
  const f = NUMBER_FMT.format(Math.abs(amount));
  return amount < 0 ? `−${f}` : f;
};

/**
 * Inline money formatter used across invoice/expense tables and detail screens.
 * Phase 4: the «≈ BYN» secondary line is gone — LearnIT operates in PLN, so
 * the BYN-equivalent (which is often `null` for PLN-only data anyway) only
 * added noise. The native currency suffix stays on the main figure.
 */
export const MoneyCell = memo(
  ({
    amount,
    currency,
    negative,
    highlightOutstanding,
    align = 'right',
  }: IMoneyCellProps) => {
    if (amount === null || amount === undefined) {
      return <Wrap align={align}>—</Wrap>;
    }
    const isNegative = negative ?? amount < 0;
    return (
      <Wrap align={align}>
        <Main negative={isNegative} red={highlightOutstanding && amount > 0}>
          {formatAmount(amount)} {currency || ''}
        </Main>
      </Wrap>
    );
  },
);

const Wrap = styled.div<{ align: 'left' | 'right' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ align }) =>
    align === 'right' ? 'flex-end' : 'flex-start'};
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
`;

const Main = styled.span<{ negative: boolean; red?: boolean }>`
  font-weight: 600;
  color: ${({ negative, red }) =>
    red ? '#dc2626' : negative ? '#64748b' : '#0f172a'};
  white-space: nowrap;
`;
